import { useNavigate } from 'react-router-dom';
import { useState, useEffect, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { useStars } from '../context/StarContext';
import StillButton from './ui/StillButton';
import { validateField } from '../utils/formValidation';
import { calculateWeeksLoved } from '../utils/dateUtils';
import { useOverlay } from '../App';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useCustomNavigation } from '../hooks/useCustomNavigation';
import VideoStar from './VideoStar';

// Custom date input component wrapped in forwardRef
const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder, className }, ref) => (
    <input
        ref={ref}
        type="text"
        value={value}
        onClick={onClick}
        onChange={onChange}
        placeholder={placeholder || "SELECT DATE"}
        className={className}
        readOnly
    />
));

CustomDateInput.displayName = 'CustomDateInput';

const CreateStarOverlay = () => {
    const navigate = useNavigate();
    const { navigateTo } = useCustomNavigation();
    const { addStar, loading, error } = useStars();
    const { toggleOverlay } = useOverlay();
    const { createStar } = useStars();
    const [selectedStar, setSelectedStar] = useState(Math.floor(Math.random() * 5) + 1);
    const [useWebM, setUseWebM] = useState(true);
    const [isSafari, setIsSafari] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        dob: '',
        pob: '',
        message: '',
        isPublicMessage: false,
        starType: null
    });
    const [formErrors, setFormErrors] = useState({});
    const [isChecked, setIsChecked] = useState(false);

    const [isMobile, setIsMobile] = useState(false);

     // Check for mobile view
     useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 800);
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Hide the overlay when this component mounts
    useEffect(() => {
        toggleOverlay(false);
        
        // Clean up when unmounting
        return () => toggleOverlay(false);
    }, [toggleOverlay]);

    // Add custom styles for the date picker
    useEffect(() => {
        // Add custom styles for the date picker
        const style = document.createElement('style');
        style.textContent = `
            .react-datepicker {
                background-color: rgba(0, 0, 0, 1) !important;
                border: 1px solid #00a0c0 !important;
                border-radius: 8px !important;
                font-family: inherit !important;
                box-shadow: 0 0 10px rgba(0, 160, 192, 0.5) !important;
            }
            .react-datepicker__header {
                background-color: rgba(0, 0, 0, 1) !important;
                border-bottom: 1px solid #00a0c0 !important;
            }
            .react-datepicker__current-month, 
            .react-datepicker__day-name, 
            .react-datepicker__navigation {
                color: #00a0c0 !important;
            }
            .react-datepicker__day {
                color: #00a0c0 !important;
            }
            .react-datepicker__day:hover {
                background-color: rgba(0, 160, 192, 0.3) !important;
            }
            .react-datepicker__day--selected, 
            .react-datepicker__day--keyboard-selected {
                background-color: #00a0c0 !important;
                color: white !important;
            }
            .react-datepicker__day--disabled {
                color: #555 !important;
            }
            .react-datepicker__triangle {
                display: none !important;
            }
            .react-datepicker__navigation-icon::before {
                border-color: #00a0c0 !important;
            }
            .react-datepicker-popper {
                z-index: 9999 !important;
            }
            .react-datepicker__input-container input {
                background-color: transparent !important;
                border: 0 !important;
                padding: 0 !important;
                font-size: 1.125rem !important;
                color: #00a0c0 !important;
                outline: none !important;
                text-transform: uppercase !important;
                width: 100% !important;
                font-family: inherit !important;
            }
            .react-datepicker__input-container input:focus {
                outline: none !important;
                box-shadow: none !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Check for Safari and WebM support
    useEffect(() => {
        const checkBrowserSupport = () => {
            const ua = window.navigator.userAgent.toLowerCase();
            const isSafariCheck = (
                (ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1) || // Desktop Safari
                /iphone|ipod|ipad/.test(ua) // iOS Safari and all iOS WebViews
            );
            setIsSafari(isSafariCheck);

            // Only check WebM support if not Safari
            if (!isSafariCheck) {
                const video = document.createElement('video');
                const canPlayWebM = video.canPlayType('video/webm; codecs="vp8,vp9"');
                setUseWebM(canPlayWebM === 'probably' || canPlayWebM === 'maybe');
            } else {
                setUseWebM(false);
            }
        };

        checkBrowserSupport();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        const validation = validateField(name, value);
        
        setFormErrors(prev => ({
            ...prev,
            [name]: validation.error
        }));
    };

    const handleClose = () => {
        navigate('/space');
    };

    const validateForm = () => {
        const errors = {};
        
        // Required field validation for all fields except message
        const requiredFields = ['name', 'dob', 'pob'];
        requiredFields.forEach(field => {
            const validation = validateField(field, formData[field]);
            if (!validation.isValid) {
                errors[field] = validation.error;
            }
        });
        
        // Only validate message if it's not empty
        if (formData.message) {
            const messageValidation = validateField('message', formData.message);
            if (!messageValidation.isValid) {
                errors.message = messageValidation.error;
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddStar = async () => {
        // Create a local copy of the form data to ensure updates are applied immediately
        let currentFormData = {...formData};
        
        // Make sure starType is set
        if (!currentFormData.starType) {
            //console.log('starType was not set, setting to selectedStar:', selectedStar);
            currentFormData = {
                ...currentFormData,
                starType: selectedStar
            };
            setFormData(currentFormData);
        }

        //console.log('Submitting form with starType:', currentFormData.starType);

        if (!validateForm()) {
            console.error('Form validation failed', formErrors);
            return;
        }

        try {
            //console.log('Submitting form data:', currentFormData);
            const savedStar = await addStar(currentFormData);
            // Use the USID from the server response which may have been modified
            navigateTo(`/confirmation/${savedStar.usid}`);
        } catch (err) {
            console.error('Error creating star:', err);
            setFormErrors(prev => ({
                ...prev,
                general: `An error occurred while creating your star: ${err.message}`
            }));
        }
    };

    const calculateStarScale = (weeksLoved) => {
        // Default to minimum scale if no valid weeks
        if (!weeksLoved || weeksLoved < 0) return 0.5;
        
        // Cap weeks at 260 for maximum scale
        const cappedWeeks = Math.min(weeksLoved, 260);
        
        // Calculate scale using linear interpolation
        // 0 weeks = 0.5 scale
        // 260 weeks = 1.8 scale
        const scale = 0.5 + (cappedWeeks / 260) * (1.8 - 0.5);
        
        return scale;
    };

    const weeksLoved = formData.dob ? calculateWeeksLoved(formData.dob) : 0;
    const starScale = calculateStarScale(weeksLoved);

    const handleCheckboxChange = (e) => {
        const { checked } = e.target;
        setIsChecked(checked);
        setFormData(prev => ({
            ...prev,
            isPublicMessage: checked
        }));
    };

    const handleStarSelect = (starNumber) => {
        //console.log('Star selected:', starNumber);
        setSelectedStar(starNumber);
        setFormData(prev => {
            const updated = {
                ...prev,
                starType: starNumber
            };
            //console.log('Updated form data after star selection:', updated);
            return updated;
        });
    };

    return (
        <div className="fixed inset-0 flex flex-col overflow-y-auto">
            <motion.div 
                className="min-h-screen w-full flex flex-col lg:flex-row items-center mt-20 p-4 lg:mt-0 lg:p-8 gap-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div 
                    className="hidden lg:flex lg:flex-1 flex-col items-center justify-center pt-16 lg:pt-0"
                    variants={containerVariants}
                >
                    <StarVideoContent 
                        starScale={starScale} 
                        selectedStar={selectedStar}
                        weeksLoved={weeksLoved}
                        itemVariants={itemVariants}
                        isSafari={isSafari}
                        useWebM={useWebM}
                    />
                </motion.div>

                <motion.div 
                    className="w-full lg:flex-1 flex flex-col justify-center px-4 lg:p-10 lg:mr-20 max-w-xl"
                    variants={containerVariants}
                >
                    <div className="space-y-6">
                        <motion.div variants={itemVariants}>
                            <h1 className="mb-4">
                                See your star <span className="text-still-white text-glow-still">shine</span>
                            </h1>
                            <p className="text-still-white text-lg lg:text-lg max-w-[450px]">
                            To add your star, simply enter your baby's name, as well as a date and place of remembrance.
                            </p>
                        </motion.div>

                        {error && (
                            <motion.div 
                                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
                                variants={itemVariants}
                            >
                                {error}
                            </motion.div>
                        )}

                        <motion.div className="space-y-6" variants={itemVariants}>
                            {/* Name Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <label className="text-still-white uppercase text-base w-1/3">Name:</label>
                                    <input
                                        type="text"
                                        name="name"
                                        maxLength={20}
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`bg-transparent border-0 p-0 focus:ring-0 text-lg text-cyan-300 focus:outline-none uppercase w-2/3 ${
                                            formErrors.name ? 'text-red-500' : ''
                                        }`}
                                        placeholder="ENTER NAME"
                                    />
                                </div>
                                {formErrors.name && (
                                    <p className="text-red-500 text-xs ml-[33%]">{formErrors.name}</p>
                                )}
                            </div>

                            {/* Date Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <label className="text-still-white uppercase text-base w-1/3">Date:</label>
                                    <DatePicker
                                        selected={formData.dob ? new Date(formData.dob) : null}
                                        onChange={(date) => {
                                            const formattedDate = date ? date.toISOString().split('T')[0] : '';
                                            handleChange({
                                                target: {
                                                    name: 'dob',
                                                    value: formattedDate
                                                }
                                            });
                                        }}
                                        maxDate={new Date()}
                                        placeholderText="SELECT DATE"
                                        customInput={
                                            <CustomDateInput 
                                                className={`bg-transparent border-0 p-0 focus:ring-0 text-lg text-cyan-300 focus:outline-none uppercase w-2/3 ${
                                                    formErrors.dob ? 'text-red-500' : ''
                                                }`}
                                            />
                                        }
                                        popperClassName="date-picker-popper"
                                        popperPlacement="bottom-end"
                                        dateFormat="dd/MM/yyyy"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        yearDropdownItemNumber={100}
                                    />
                                </div>
                                {formErrors.dob && <p className="text-red-500 text-xs ml-[33%]">{formErrors.dob}</p>}
                            </div>

                            {/* Place Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <label className="text-still-white uppercase text-base w-1/3">Place:</label>
                                    <input
                                        type="text"
                                        name="pob"
                                        maxLength={20}
                                        value={formData.pob}
                                        onChange={handleChange}
                                        className={`bg-transparent border-0 p-0 focus:ring-0 text-lg text-cyan-300 focus:outline-none uppercase w-2/3 ${
                                            formErrors.pob ? 'text-red-500' : ''
                                        }`}
                                        placeholder="ENTER PLACE"
                                    />
                                </div>
                                {formErrors.pob && <p className="text-red-500 text-xs ml-[33%]">{formErrors.pob}</p>}
                            </div>

                            {/* Star Selection */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <label className="text-still-white uppercase text-base w-1/3">Choose a Star:</label>
                                    <div className="flex gap-6 w-2/3">
                                        {[1, 2, 3, 4, 5].map((num) => {
                                            const videoPath = isSafari
                                                ? `/assets/stars/fallback/Star_${num}.mov`
                                                : `/assets/stars/${useWebM ? 'webm' : 'fallback'}/Star_${num}.${useWebM ? 'webm' : 'mp4'}`;
                                            
                                            return (
                                                <button
                                                    key={num}
                                                    onClick={() => handleStarSelect(num)}
                                                    className={`relative w-8 h-8 overflow-hidden rounded-full hover:ring-2 hover:ring-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-300
                                                        ${selectedStar === num ? 'ring-2 ring-cyan-300' : ''}`}
                                                >
                                                    <video
                                                        autoPlay
                                                        loop
                                                        muted
                                                        playsInline
                                                        disablePictureInPicture
                                                        className="w-full h-full object-cover mix-blend-screen"
                                                        src={videoPath}
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Message Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex flex-col">
                                    <label className="text-still-white uppercase text-base mb-1">Message</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        maxLength={210}
                                        rows={2}
                                        className={`bg-transparent border-0 p-0 focus:ring-0 text-lg text-cyan-300 focus:outline-none resize-none ${
                                            formErrors.message ? 'text-red-500' : ''
                                        }`}
                                        placeholder="ENTER AN OPTIONAL MESSAGE"
                                    />
                                </div>
                                {formErrors.message && <p className="text-red-500 text-xs">{formErrors.message}</p>}
                            </div>

                            {formData.message && (
                                <div className="flex items-start gap-2 mt-1">
                                    <input
                                        type="checkbox"
                                        id="publicMessage"
                                        name="publicMessage"
                                        checked={isChecked}
                                        onChange={handleCheckboxChange}
                                        aria-checked={isChecked}
                                        className="w-4 h-4 mt-1 rounded border-gray-300 text-cyan-300 focus:ring-cyan-300"
                                    />
                                    <label 
                                        htmlFor="publicMessage" 
                                        className="text-still-white text-xs leading-normal"
                                    >
                                        I consent to my message being publicly visible to other visitors
                                    </label>
                                </div>
                            )}

                            <motion.div 
                                className="lg:hidden flex flex-col items-center justify-center mt-8"
                                variants={containerVariants}
                            >
                                <StarVideoContent 
                                    starScale={starScale} 
                                    selectedStar={selectedStar}
                                    weeksLoved={weeksLoved}
                                    itemVariants={itemVariants}
                                    isSafari={isSafari}
                                    useWebM={useWebM}
                                />
                            </motion.div>

                            <div className="flex flex-col sm:flex-row gap-4 pb-40 sm:space-x-4 md:pb-10 mt-8">
                            <StillButton 
                                    onClick={handleAddStar}
                                    disabled={loading}
                                    aria-label="ADD YOUR STAR"
                                    className="w-full sm:w-auto"
                                >
                                    {loading ? 'ADDING...' : 'ADD YOUR STAR'}
                                </StillButton>
                                <StillButton 
                                    onClick={handleClose}
                                    disabled={loading}
                                    aria-label="Close"
                                    className="w-full sm:w-auto"
                                >
                                    CLOSE
                                </StillButton>
                            </div>
                            
                            {/* Mobile button underlay */}
                            {isMobile && (
                                <div className="fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-none z-50 pt-28 pb-20 py-4 bg-gradient-to-t from-black via-black to-transparent h-22">
                                </div>
                            )}
                        </motion.div>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

const StarVideoContent = ({ starScale, selectedStar, weeksLoved, itemVariants, isSafari, useWebM }) => {
    const getVideoPath = () => {
        if (isSafari) {
            // Use .mov for Safari (HEVC with alpha)
            return `/assets/stars/fallback/Star_${selectedStar}.mov`;
        }
        
        // Use WebM for other browsers, fallback to MP4 if WebM not supported
        const format = useWebM ? 'webm' : 'fallback';
        const extension = useWebM ? 'webm' : 'mp4';
        return `/assets/stars/${format}/Star_${selectedStar}.${extension}`;
    };

    return (
        <div className="flex flex-col items-center">
            {/* Container for the star video with fixed dimensions */}
            <div className="relative w-48 h-48 lg:w-64 lg:h-64 flex items-center justify-center">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ 
                        transform: `scale(${starScale})`,
                        transition: 'transform 0.5s ease-out'
                    }}
                    disablePictureInPicture
                    className="max-w-full h-auto mix-blend-screen"
                    src={getVideoPath()}
                />
            </div>
            {/* Weeks still loved text */}
            <div className="h-16 mt-12">
                <motion.p 
                    className="text-still-white text-glow-still text-2xl lg:text-4xl uppercase"
                    variants={itemVariants}
                >
                    {weeksLoved} weeks still loved
                </motion.p>
            </div>
        </div>
    );
};

export default CreateStarOverlay; 